import type {
  BulkImportItem,
  BulkImportItemReferenceType,
  BulkImportItemStatus,
  BulkImportStatus
} from "@gettactic/api";
import type { PaginatedResult } from "@gettactic/api/src/endpoints/response/paginated_result";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useTacticHooks } from "../../providers/TacticHooksProvider";

export const BULK_IMPORT_ITEMS_QUERY_KEY = "useBulkImportItemsQuery";

const PAGE_SIZE = 50;
const ACTIVE_POLL_INTERVAL_MS = 2000;

type Params = {
  status?: BulkImportItemStatus;
  referenceType?: BulkImportItemReferenceType;
};

export const useBulkImportItemsQuery = (
  bulkImportId: string | null | undefined,
  params: Params = {},
  parentStatus?: BulkImportStatus
) => {
  const { apiClient } = useTacticHooks();
  const queryClient = useQueryClient();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery<PaginatedResult<BulkImportItem> | null>({
      queryKey: [
        BULK_IMPORT_ITEMS_QUERY_KEY,
        apiClient.slug,
        bulkImportId,
        params.status,
        params.referenceType
      ],
      queryFn: async ({ pageParam }) => {
        if (!bulkImportId) {
          return null;
        }
        const res = await apiClient.bulkImports.listItems(bulkImportId, {
          cursor: pageParam as string | undefined,
          limit: PAGE_SIZE,
          status: params.status,
          reference_type: params.referenceType
        });
        return res.result;
      },
      initialPageParam: undefined,
      getNextPageParam: (lastPage) =>
        (lastPage?.elements.length ?? 0) < PAGE_SIZE
          ? undefined
          : (lastPage?.cursor ?? undefined),
      enabled: !!apiClient.slug && !!bulkImportId,
      refetchInterval:
        parentStatus === "queued" || parentStatus === "processing"
          ? ACTIVE_POLL_INTERVAL_MS
          : false
    });

  useEffect(() => {
    if (parentStatus !== "completed" && parentStatus !== "failed") {
      return;
    }
    void queryClient.invalidateQueries({
      queryKey: [BULK_IMPORT_ITEMS_QUERY_KEY, apiClient.slug, bulkImportId]
    });
  }, [parentStatus, queryClient, apiClient.slug, bulkImportId]);

  const allItems = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page?.elements ?? []),
    [data]
  );

  return {
    allItems,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading
  };
};
